import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Card,
  CardMedia,
  alpha
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ImageSearchIcon from '@mui/icons-material/ImageSearch'
import AssetLoader from '../utils/AssetLoader'
import CheckIcon from '@mui/icons-material/Check'
import FaceIcon from '@mui/icons-material/Face'
import { HighQuality, AutoAwesome } from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { useProjects, ProjectProvider } from '../contexts/ProjectContext'
import { DesignerProvider, useDesigner } from '../contexts/DesignerContext'
import AppHeader from '../components/common/AppHeader'
import { APP_VERSION } from '../config/version'
import ModelSelection from '../components/designer/ModelSelection'
import BodyPartSelection from '../components/designer/BodyPartSelection'
import GarmentTransferSelection from '../components/designer/GarmentTransferSelection'
import ModelUploader from '../components/designer/ModelUploader'
import SourceImageUploader from '../components/designer/SourceImageUploader'
import QualitySelector from '../components/designer/QualitySelector'
import AdminControls from '../components/designer/AdminControls'
import ErrorBoundary from '../components/common/ErrorBoundary'
import GeneratedImageViewer, { GeneratedImage } from '../components/ImageGeneration/GeneratedImageViewer'
import ProgressIndicator from '../components/designer/ProgressIndicator'
import StepSection from '../components/designer/StepSection'

// Referencja do typów transferu dla podsumowania w UI on-model
const transferTypes = [
  { value: 'generic', label: 'Całe ubranie' },
  { value: 'upper', label: 'Góra' },
  { value: 'lower', label: 'Dół' },
  { value: 'dress', label: 'Sukienka' },
  { value: 't-shirt', label: 'T-shirt' },
  { value: 'pants', label: 'Spodnie' },
  { value: 'jacket', label: 'Kurtka/Płaszcz' },
  { value: 'body', label: 'Całe ciało' }
];

// Helper function to generate mask file URLs from a GeneratedImage
const generateMaskFiles = (image: GeneratedImage) => {
  const imageUrl = image.url;
  if (!imageUrl) return {};
  
  // Change /images/ to /masks/ in path to get mask directory
  const maskBasePath = imageUrl.replace('/images/', '/masks/');
  
  // Extract directory path
  const maskDir = maskBasePath.substring(0, maskBasePath.lastIndexOf('/'));
  
  // For unified ComfyUI naming structure (new format)
  if (imageUrl.includes('output-preview-garment-and-human.png')) {
    return {
      humanMask: `${maskDir}/human-mask.png`,
      humanMaskExpanded: `${maskDir}/human-mask-after-expand.png`,
      humanImageMask: `${maskDir}/human-image-mask.png`,
      garmentMask: `${maskDir}/garment-mask.png`
    };
  }
  
  // If filename looks like a ComfyUI temp file saved to S3 (e.g., 20250926-171654-ComfyUI_temp_xxxxx_00001_.png),
  // skip synthetic mask generation — masks are not saved alongside these outputs by default.
  const baseNameOnly = imageUrl.split('/').pop() || '';
  if (/^\d{8}-\d{6}-ComfyUI_temp_.+\.png$/.test(baseNameOnly)) {
    return {};
  }

  // Fallback to old structure for backward compatibility (expects names like output-YYYYMMDD-HHMMSS-...)
  const filename = maskBasePath.split('/').pop() || '';
  const filenameNoExt = filename.replace(/\.png$/, '');
  
  // Extract base name: output-YYYYMMDD-HHMMSS from pattern like output-20250614-125519-186-Preview Image
  const parts = filenameNoExt.split('-');
  let baseName = '';
  
  if (parts.length >= 3 && parts[0] === 'output') {
    // Take first 3 parts: output-YYYYMMDD-HHMMSS
    baseName = parts.slice(0, 3).join('-');
  } else {
    // Unknown pattern — do not generate mask paths
    // console.warn('Unable to parse image filename for mask generation:', filename);
    return {};
  }
  
  return {
    humanMask: `${maskDir}/${baseName}-165-human-mask.png`,
    humanMaskExpanded: `${maskDir}/${baseName}-166-human-mask-after-expand.png`,
    humanImageMask: `${maskDir}/${baseName}-162-human-image-mask.png`,
    garmentMask: `${maskDir}/${baseName}-175-garment-mask.png`
  };
};

const DesignerContent: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const location = useLocation()
  const { user } = useAuth()
  const { projects, currentProject, loading, loadProjects, selectProject } = useProjects()
  const { 
    selections, 
    setModelType, 
    setModelImage, 
    setStudio, 
    setGarmentType,
    setGarment,
    setQuality,
    setSourceImage,
    setTransferMode,
    setGarmentMaskPrompt,
    setHumanMaskPrompt,
    generateImageFromSelections, 
    generationResult, 
    isGenerating 
  } = useDesigner()
  const navigate = useNavigate()
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [useCustomModel, setUseCustomModel] = useState<boolean>(false)
  
  // Calculate current step for progress indicator
  const getCurrentStep = () => {
    if (isOnModelMode) {
      if (!selections.sourceImage) return 0;
      if (!selections.garmentType) return 1;
      if (!selections.modelImage) return 2;
      if (!selections.studio) return 3;
      if (generationResult.status === 'generating' || isGenerating) return 5;
      return 4;
    } else {
      if (!selections.modelImage) return 0;
      if (!selections.garmentType) return 1;
      if (!selections.garment[selections.garmentType || '']) return 2;
      if (!selections.studio) return 3;
      if (generationResult.status === 'generating' || isGenerating) return 4;
      return 4;
    }
  };
  
  // Get the mode from URL query parameters
  const queryParams = new URLSearchParams(location.search)
  const mode = queryParams.get('mode') || 'on-model'
  
  // Flag to determine if we're in "On-Model" or "Flat-Lay" mode
  const isOnModelMode = mode === 'on-model'

  // Ensure project from URL is selected in context
  useEffect(() => {
    if (projectId && !currentProject) {
      selectProject(projectId)
    }
  }, [projectId, currentProject])

  // Studio preview images cache
  const [studioPreviews, setStudioPreviews] = useState<Record<string, string>>({})

  useEffect(() => {
    const types = ['office','real','yacht']
    const go = async () => {
      const out: Record<string,string> = {}
      for (const t of types) {
        const imgs = await AssetLoader.loadImages(`studio/${t}`)
        if (imgs && imgs.length > 0) {
          out[t] = imgs[0]
        } else {
          // Fallbacks aligned to existing public/studio assets
          const fallback = (type: string) => {
            if (type === 'office') return '/studio/office/modern-office1.jpg'
            if (type === 'real') return '/studio/real/real-do-opisu-biuro.jpeg'
            if (type === 'yacht') return '/studio/yacht/yacht1.jpg'
            return `/studio/${type}/${type}1.jpg`
          }
          out[t] = fallback(t)
        }
      }
      setStudioPreviews(out)
    }
    go()
  }, [])

  // Load existing images when component mounts or project changes
  useEffect(() => {
    const loadExistingImages = async () => {
      if (!user?.username || !currentProject?.name) {
        console.log('Skipping image load: user or project not available', { user: user?.username, project: currentProject?.name });
        return;
      }
      
      console.log(`Loading existing images for user: ${user.username}, project: ${currentProject.name}`);
      
      try {
        const url = `/api/list_generations?username=${user.username}&project=${currentProject.name}`;
        console.log('Fetching from URL:', url);
        const response = await fetch(url);
        const data = await response.json();
        console.log('API response:', data);
        
        if (response.ok && data.status === 'success' && Array.isArray(data.generations)) {
          // Convert generations to GeneratedImage objects
          const loadedImages: GeneratedImage[] = data.generations.map((generation: any) => {
            // Use the URL directly from the generation object (backend already finds the correct image)
            const imageUrl = generation.url;
            
            if (!imageUrl) {
              return null; // Skip generations without images
            }
            
            return {
              id: generation.id,
              url: imageUrl,
              timestamp: generation.timestamp,
              metadata: {
                quality: generation.metadata?.quality || 'high' as const,
                transferType: generation.metadata?.transferType,
                modelUsed: generation.metadata?.modelUsed,
                elapsedTime: generation.metadata?.elapsedTime,
                width: generation.metadata?.width,
                height: generation.metadata?.height
              },
              maskFiles: generation.maskFiles || {}
            };
          }).filter((image): image is GeneratedImage => image !== null);
          
          // Sort by timestamp (newest first)
          loadedImages.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          
          console.log(`Loaded ${loadedImages.length} existing images for project ${currentProject.name}`);
          setGeneratedImages(loadedImages);
        }
      } catch (error) {
        console.error('Error loading existing images:', error);
      }
    };
    
    loadExistingImages();
  }, [user?.username, currentProject?.name]);

  // Update generatedImages when generationResult changes
  useEffect(() => {
    if (generationResult.status === 'success' && generationResult.previewUrl) {
      const allPreviews = generationResult.previewUrls || [generationResult.previewUrl]

      // Categorize images by filename patterns
      const mainImage = allPreviews.find(p => p.includes('output-preview-garment-and-human')) || generationResult.previewUrl
      const maskFiles: GeneratedImage['maskFiles'] = {}

      allPreviews.forEach(url => {
        if (url.includes('human-mask-expanded')) {
          maskFiles.humanMaskExpanded = url
        } else if (url.includes('human-mask-image')) {
          maskFiles.humanImageMask = url
        } else if (url.includes('garment-mask')) {
          maskFiles.garmentMask = url
        } else if (url.includes('output-preview-human-saved-image')) {
          maskFiles.humanMask = url
        }
      })

      const newImage: GeneratedImage = {
        id: generationResult.metadata?.generationId || Date.now().toString(),
        url: mainImage,
        timestamp: generationResult.metadata?.timestamp || new Date().toISOString(),
        metadata: generationResult.metadata,
        maskFiles
      };

      // If no maskFiles found, generate them as fallback
      if (Object.keys(maskFiles).length === 0 && (!generationResult.metadata?.maskFiles || Object.keys(generationResult.metadata.maskFiles).length === 0)) {
        newImage.maskFiles = generateMaskFiles(newImage);
      }

      console.log('Adding new generated image with masks:', newImage);
      setGeneratedImages(prev => [newImage, ...prev]); // Add new image at the beginning
    }
  }, [generationResult.status, generationResult.previewUrl, generationResult.previewUrls, generationResult.metadata, selections.positivePrompt]);

  const handleSelectStudio = (studio: string) => {
    setStudio(studio)
  }
  
  const handleGarmentTransferTypeSelect = (type: string, garmentMaskPrompt: string, humanMaskPrompt: string) => {
    setGarmentType(type)
    setGarmentMaskPrompt(garmentMaskPrompt)
    setHumanMaskPrompt(humanMaskPrompt)
  }
  
  const handleSourceImageSelect = (image: string) => {
    setSourceImage(image)
  }
  
  const handleTargetModelSelect = (useCustom: boolean) => {
    setUseCustomModel(useCustom)
  }
  
  const handleGenerateClick = async () => {
    if (projectId) {
      const success = await generateImageFromSelections(projectId)
      
      if (success && generationResult.previewUrl) {
        // Add the newly generated image to the list
        const newImage: GeneratedImage = {
          id: `img-${Date.now()}`,
          url: generationResult.previewUrl,
          timestamp: new Date().toISOString(),
          metadata: {
            transferType: selections.garmentType || undefined,
            quality: selections.quality,
            elapsedTime: generationResult.elapsedTime
          },
          maskFiles: {}
        }
        
        setGeneratedImages((prev: GeneratedImage[]) => [newImage, ...prev])
      }
    }
  }
  
  // Save an image to the project
  const handleSaveImage = (image: GeneratedImage) => {
    // Implement save functionality
    console.log('Saving image:', image)
    // Here you would call an API to save the image to the project
  }

  // Delete an image and associated masks

  const handleDeleteImage = async (image: GeneratedImage) => {
    try {
      console.log('Deleting generation:', image)
      
      const response = await fetch(`/api/delete_generation?username=${user?.username}&project=${currentProject?.name}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generation_id: image.id
        })
      })

      const result = await response.json()

      if (result.status === 'success') {
        console.log('Image deleted successfully:', result.message)
        // Remove the image from the local state
        setGeneratedImages(prev => prev.filter(img => img.id !== image.id))
      } else {
        console.error('Delete failed:', result.message)
        alert(`Delete failed: ${result.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Delete error:', error)
      alert(`Delete error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
  
  if (loading || !currentProject) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }
  
  return (
    <ErrorBoundary>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Progress Indicator */}
        <ProgressIndicator 
          currentStep={getCurrentStep()}
          mode={isOnModelMode ? 'on-model' : 'flat-lay'}
        />
        {isOnModelMode ? (
          // ON-MODEL MODE - Interface for transferring clothing from a real image to a virtual model
          <>
            {/* STEP 1: SOURCE IMAGE UPLOAD */}
            <StepSection
              title="Zdjęcie źródłowe"
              subtitle="Prześlij zdjęcie z odzieżą do przeniesienia"
              icon={<CloudUploadIcon />}
              stepNumber={1}
              active={getCurrentStep() === 0}
              completed={!!selections.sourceImage}
              helpText="Wybierz wysokiej jakości zdjęcie z wyraźnie widoczną odzieżą. Najlepsze rezultaty uzyskasz ze zdjęciami na jednolitym tle."
            >
              <SourceImageUploader 
                username={user?.username || ''}
                projectName={currentProject?.name || 'default'}
                onImageUpload={(imagePath) => {
                  console.log("SourceImageUploader onImageUpload called with:", imagePath);
                  setSourceImage(imagePath);
                }}
              />
            </StepSection>
            
            {/* STEP 2: GARMENT TRANSFER TYPE */}
            <StepSection
              title="Typ przeniesienia"
              subtitle="Wybierz, którą część odzieży chcesz przenieść"
              icon={<ImageSearchIcon />}
              stepNumber={2}
              active={getCurrentStep() === 1}
              completed={!!selections.garmentType}
              helpText="Wybierz typ elementu, który ma zostać przeniesiony z oryginalnego zdjęcia na model docelowy."
              sx={{ display: selections.sourceImage ? 'block' : 'none' }}
            >
              <GarmentTransferSelection 
                onTransferTypeSelect={handleGarmentTransferTypeSelect}
                sourceImageUploaded={!!selections.sourceImage}
              />
            </StepSection>
            
            {/* STEP 3: MODEL SELECTION */}
            {selections.sourceImage && selections.garmentType && (
              <StepSection
                title="Model docelowy"
                subtitle="Wybierz model, na którego zostanie przeniesiona odzież"
                icon={<FaceIcon />}
                stepNumber={3}
                active={getCurrentStep() === 2}
                completed={!!selections.modelImage}
                helpText="Możesz wybrać model z naszej galerii lub przesłać własne zdjęcie modela."
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Wybierz źródło modela docelowego:
                  </Typography>
                  <RadioGroup
                    row
                    value={useCustomModel ? 'custom' : 'gallery'}
                    onChange={(e) => setUseCustomModel(e.target.value === 'custom')}
                  >
                    <FormControlLabel 
                      value="gallery" 
                      control={<Radio />} 
                      label="Wybierz z galerii" 
                      sx={{ 
                        mr: 4,
                        '& .MuiFormControlLabel-label': {
                          fontWeight: !useCustomModel ? 600 : 400
                        }
                      }}
                    />
                    <FormControlLabel 
                      value="custom" 
                      control={<Radio />} 
                      label="Prześlij własne zdjęcie modela" 
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontWeight: useCustomModel ? 600 : 400
                        }
                      }}
                    />
                  </RadioGroup>
                </Box>

                {/* SELECT TARGET MODEL FROM GALLERY */}
                {!useCustomModel ? (
                  <ModelSelection 
                    onSelectModel={(modelData) => {
                      console.log("ModelSelection onSelectModel called with:", modelData);
                      setModelType(modelData.type)
                      setModelImage(modelData.image, modelData.isCustom)
                      
                      // Dodanie obsługi garmentType
                      if (modelData.metadata?.garmentType) {
                        console.log("Setting garment type:", modelData.metadata.garmentType);
                        setGarmentType(modelData.metadata.garmentType);
                      }
                    }}
                    projectName={currentProject?.name || 'default'}
                    username={user?.username || ''}
                  />
                ) : (
                  /* CUSTOM MODEL UPLOAD */
                  <ModelUploader 
                    username={user?.username || ''} 
                    projectName={currentProject?.name || 'default'}
                    onModelSelect={(image) => setModelImage(image, true)}
                  />
                )}
              </StepSection>
            )}
            
            {/* STEP 4: STUDIO SELECTION */}
            {selections.modelImage && selections.sourceImage && (
              <StepSection
                title="Studio"
                subtitle="Wybierz tło i oświetlenie dla finalnego zdjęcia"
                icon={<ImageSearchIcon />}
                stepNumber={4}
                active={getCurrentStep() === 3}
                completed={!!selections.studio}
                helpText="Wybierz studio, które najlepiej pasuje do stylu Twojego projektu."
              >
                <Grid container spacing={3}>
                  {['office', 'real', 'yacht'].map((studioType) => (
                    <Grid item xs={12} sm={6} md={4} key={studioType}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          boxShadow: selections.studio === studioType 
                            ? '0 0 0 3px #1976d2, 0 8px 24px rgba(0,0,0,0.15)' 
                            : '0 2px 8px rgba(0,0,0,0.08)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: selections.studio === studioType 
                              ? '0 0 0 3px #1976d2, 0 12px 28px rgba(0,0,0,0.18)' 
                              : '0 8px 24px rgba(0,0,0,0.12)',
                          }
                        }}
                        onClick={() => handleSelectStudio(studioType)}
                      >
                        <CardMedia
                          component="img"
                          height={200}
                          image={studioPreviews[studioType] || (studioType === 'office' ? '/studio/office/modern-office1.jpg' : studioType === 'real' ? '/studio/real/real-do-opisu-biuro.jpeg' : studioType === 'yacht' ? '/studio/yacht/yacht1.jpg' : `/studio/${studioType}/${studioType}1.jpg`)}
                          alt={`Studio ${studioType}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        
                        {/* Studio name */}
                        <Box sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(0,0,0,0.03)' }}>
                          <Typography variant="h6" fontWeight={600}>
                            {studioType.charAt(0).toUpperCase() + studioType.slice(1)}
                          </Typography>
                        </Box>
                        
                        {/* Selection indicator */}
                        {selections.studio === studioType && (
                          <Box sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: '#1976d2',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.3)'
                          }}>
                            <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </StepSection>
            )}
            
            {/* STEP 5: QUALITY SELECTION */}
            <StepSection
              title="Jakość generowania"
              subtitle="Wybierz jakość i szybkość generowania obrazu"
              icon={<HighQuality />}
              stepNumber={5}
              active={getCurrentStep() === 4}
              completed={!!(selections.modelImage && selections.sourceImage && selections.studio)}
              helpText="Wyższa jakość daje lepsze rezultaty, ale trwa dłużej. Niska jakość jest szybsza, ale może być mniej precyzyjna."
              sx={{ display: !!(selections.modelImage && selections.sourceImage && selections.studio) ? 'block' : 'none' }}
            >
              <QualitySelector 
                onQualitySelect={(quality) => setQuality(quality)}
                readyToGenerate={!!(selections.modelImage && selections.sourceImage && selections.studio)}
              />
            </StepSection>
            
            {/* ADMIN CONTROLS - Only visible for admin users */}
            <AdminControls />
            
            {/* STEP 6: GENERATE BUTTON AND PROGRESS */}
            {selections.modelImage && selections.sourceImage && selections.studio && (
              <StepSection
                title="Generowanie obrazu"
                subtitle="Uruchom proces tworzenia końcowego obrazu"
                icon={<AutoAwesome />}
                stepNumber={6}
                active={getCurrentStep() === 5}
                completed={generationResult.status === 'success'}
                helpText="Proces generowania może potrwać 20-60 sekund w zależności od wybranej jakości."
              >
                  {/* Current Selections Summary */}
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      backgroundColor: alpha('#e3f2fd', 0.5),
                      borderRadius: 2,
                      border: '1px solid #bbdefb'
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Podsumowanie wyborów:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Rodzaj przeniesienia:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {transferTypes.find(t => t.value === selections.garmentType)?.label || 'Nie wybrano'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Model docelowy:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selections.isCustomModel ? 'Własny model' : `Galeria: ${selections.model || 'Nie wybrano'}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Studio:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selections.studio ? (selections.studio.charAt(0).toUpperCase() + selections.studio.slice(1)) : 'Nie wybrano'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Jakość:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selections.quality === 'low' ? 'Niska (szybciej)' : 'Wysoka (lepsza jakość)'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Generate Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleGenerateClick}
                      disabled={isGenerating}
                      startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : null}
                      sx={{ 
                        py: 1.5, 
                        px: 4, 
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                    >
                      {isGenerating ? 'Generowanie...' : 'Generuj obraz'}
                    </Button>
                  </Box>
                  
                  {/* Progress Bar */}
                  {generationResult.status === 'generating' && (
                    <Box sx={{ mt: 3, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Generowanie w toku...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {generationResult.elapsedTime}s
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, overflow: 'hidden' }}>
                        <Box 
                          sx={{ 
                            width: `${generationResult.progress}%`, 
                            height: 8, 
                            bgcolor: 'primary.main',
                            transition: 'width 0.3s ease'
                          }} 
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                        Generowanie może potrwać 20-60 sekund w zależności od wybranej jakości
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Results Alert */}
                  {generationResult.message && generationResult.status !== 'generating' && (
                    <Alert 
                      severity={generationResult.status === 'success' ? 'success' : 'error'}
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      {generationResult.message}
                    </Alert>
                  )}
              </StepSection>
            )}
            
            {/* GENERATED IMAGES PREVIEW */}
            {generatedImages.length > 0 && (
              <StepSection
                title="Wygenerowane obrazy"
                subtitle="Przeglądaj, pobieraj i zarządzaj utworzonymi obrazami"
                icon={<ImageSearchIcon />}
                completed={generatedImages.length > 0}
              >
                <GeneratedImageViewer 
                  images={generatedImages}
                  loading={isGenerating}
                  error={generationResult.status === 'error' ? generationResult.message : null}
                  onSaveImage={handleSaveImage}
                  onDeleteImage={handleDeleteImage}
                />
              </StepSection>
            )}
          </>
        ) : (
          // FLAT-LAY MODE (ORIGINAL IMPLEMENTATION)
          <>
            {/* MODEL SELECTION */}
            <ModelSelection 
              onSelectModel={(modelData) => {
                console.log("ModelSelection onSelectModel called with:", modelData);
                setModelType(modelData.type)
                setModelImage(modelData.image, modelData.isCustom)
                
                // Dodanie obsługi garmentType
                if (modelData.metadata?.garmentType) {
                  console.log("Setting garment type:", modelData.metadata.garmentType);
                  setGarmentType(modelData.metadata.garmentType);
                }
              }}
              projectName={currentProject?.name || 'default'}
              username={user?.username || ''}
            />

            {/* BODY PART SELECTION */}
            {selections.modelImage && <BodyPartSelection />}

            {/* GARMENT UPLOAD */}
            {selections.garmentType && (
              <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
                {/* Header Section */}
                <Box sx={{ 
                  p: 3, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
                  backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <CloudUploadIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
                    Upload Garment
                  </Typography>
                </Box>
                
                <Box sx={{ p: 3 }}>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Please upload a garment image for the selected type: <strong>{selections.garmentType}</strong>
                  </Alert>
                  
                  <Box sx={{ 
                    border: '2px dashed rgba(25, 118, 210, 0.3)', 
                    borderRadius: 2, 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: 'rgba(25, 118, 210, 0.02)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'rgba(25, 118, 210, 0.5)',
                      bgcolor: 'rgba(25, 118, 210, 0.05)'
                    }
                  }}>
                    <CloudUploadIcon sx={{ fontSize: 60, color: 'rgba(25, 118, 210, 0.6)', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Drag and drop your garment image here or click to browse
                    </Typography>
                    
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="garment-file-input"
                      type="file"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          try {
                            // Create FormData
                            const formData = new FormData()
                            formData.append('file', file)
                    
                            // Make API call to upload garment - new directory structure
                            const response = await fetch(`/api/upload_garment?username=${user?.username}&project=${currentProject?.name}`, {
                              method: 'POST',
                              body: formData
                            })
                    
                            const result = await response.json()
                    
                            if (result.status === 'success') {
                              // Update the garment in selections
                              setGarment(selections.garmentType || 'unknown', result.path)
                            } else {
                              console.error('Upload failed:', result.message)
                              // Display error message
                              alert(`Upload failed: ${result.message || 'Unknown error'}`)
                            }
                          } catch (error) {
                            console.error('Upload error:', error)
                            alert(`Upload error: ${error instanceof Error ? error.message : 'Unknown error'}`)
                          }
                        }
                      }}
                  />
                  <label htmlFor="garment-file-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={{ mt: 2, px: 3, py: 1, borderRadius: 2 }}
                    >
                      Select File
                    </Button>
                  </label>

                  {/* Preview uploaded garment */}
                  {selections.garment[selections.garmentType || ''] && (
                    <Box mt={3} sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                        Uploaded Garment:
                      </Typography>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          display: 'inline-block', 
                          p: 2, 
                          border: '1px solid rgba(0,0,0,0.1)',
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }}
                      >
                        <img 
                          src={selections.garment[selections.garmentType || '']}
                          alt="Uploaded garment"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px', 
                            objectFit: 'contain',
                            borderRadius: 4
                          }}
                        />
                      </Paper>
                    </Box>
                  )}
                </Box>
                </Box>
              </Paper>
            )}

            {/* STUDIO SELECTION */}
            {selections.garment[selections.garmentType || ''] && (
              <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
                {/* Header Section */}
                <Box sx={{ 
                  p: 3, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
                  backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <ImageSearchIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
                    Select Studio
                  </Typography>
                </Box>
                
                {/* Studios Grid */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {['office', 'real', 'yacht'].map((studioType) => (
                      <Grid item xs={12} sm={6} md={4} key={studioType}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            boxShadow: selections.studio === studioType 
                              ? '0 0 0 3px #1976d2, 0 8px 24px rgba(0,0,0,0.15)' 
                              : '0 2px 8px rgba(0,0,0,0.08)',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: selections.studio === studioType 
                                ? '0 0 0 3px #1976d2, 0 12px 28px rgba(0,0,0,0.18)' 
                                : '0 8px 24px rgba(0,0,0,0.12)',
                            }
                          }}
                          onClick={() => handleSelectStudio(studioType)}
                        >
                          <CardMedia
                            component="img"
                            height={220}
                            image={studioPreviews[studioType] || `/studio/${studioType}/${studioType}1.png`}
                            alt={`Studio ${studioType}`}
                            sx={{ objectFit: 'cover' }}
                          />
                          
                          {/* Studio name */}
                          <Box sx={{ p: 1, textAlign: 'center', backgroundColor: 'rgba(0,0,0,0.03)' }}>
                            <Typography variant="body1" fontWeight={500}>
                              {studioType.charAt(0).toUpperCase() + studioType.slice(1)}
                            </Typography>
                          </Box>
                          
                          {/* Selection indicator */}
                          {selections.studio === studioType && (
                            <Box sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              backgroundColor: '#1976d2',
                              borderRadius: '50%',
                              width: 26,
                              height: 26,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                            }}>
                              <CheckIcon sx={{ color: 'white', fontSize: 18 }} />
                            </Box>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            )}

            {/* GENERATION CONTROLS */}
            {selections.studio && (
              <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
                {/* Header Section */}
                <Box sx={{ 
                  p: 3, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
                  backgroundColor: 'rgba(25, 118, 210, 0.03)', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <CloudUploadIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
                    Generation Controls
                  </Typography>
                </Box>
                
                <Box sx={{ p: 3 }}>
                  {/* Quality Selection */}
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <ImageSearchIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                      Quality
                    </Typography>
                    
                    <RadioGroup
                      row
                      value={selections.quality}
                      onChange={(e) => setQuality(e.target.value as 'low' | 'high')}
                    >
                      <FormControlLabel 
                        value="low" 
                        control={<Radio color="primary" />} 
                        label="Low (Faster)" 
                        sx={{ 
                          mr: 4,
                          '& .MuiFormControlLabel-label': {
                            fontWeight: selections.quality === 'low' ? 600 : 400
                          }
                        }}
                      />
                      <FormControlLabel 
                        value="high" 
                        control={<Radio color="primary" />} 
                        label="High (Better Quality)" 
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontWeight: selections.quality === 'high' ? 600 : 400
                          }
                        }}
                      />
                    </RadioGroup>
                  </Box>
                  
                  {/* Generate Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleGenerateClick}
                      disabled={isGenerating}
                      startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : null}
                      sx={{ 
                        py: 1.5, 
                        px: 4, 
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Image'}
                    </Button>
                  </Box>
                  
                  {/* Progress Bar and Results */}
                  {generationResult.status === 'generating' && (
                    <Box sx={{ mt: 3, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Generowanie w toku...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {generationResult.elapsedTime}s
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, overflow: 'hidden' }}>
                        <Box 
                          sx={{ 
                            width: `${generationResult.progress}%`, 
                            height: 8, 
                            bgcolor: 'primary.main',
                            transition: 'width 0.3s ease'
                          }} 
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                        Generowanie może potrwać 20-60 sekund w zależności od wybranej jakości
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Results Alert */}
                  {generationResult.message && generationResult.status !== 'generating' && (
                    <Alert 
                      severity={generationResult.status === 'success' ? 'success' : 'error'}
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      {generationResult.message}
                    </Alert>
                  )}
                  
                  {generationResult.previewUrl && generationResult.status === 'success' && (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom align="center">
                        Generated Image
                      </Typography>
                      
                      <Grid container spacing={3}>
                        {/* Image Preview */}
                        <Grid item xs={12} md={8}>
                          <Box 
                            sx={{ 
                              p: 2, 
                              border: '1px solid rgba(0, 0, 0, 0.1)', 
                              borderRadius: 2,
                              backgroundColor: 'white',
                              textAlign: 'center',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {generationResult.previewUrl ? (
                              <>
                                <img 
                                  src={generationResult.previewUrl} 
                                  alt="Generated Preview" 
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: 500, 
                                    objectFit: 'contain',
                                    borderRadius: 4
                                  }} 
                                  onError={(e) => {
                                    console.error("Image failed to load:", e);
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null; // Prevent infinite loop
                                    
                                    // Try alternative URL format with a fixed path structure
                                    const originalSrc = target.src;
                                    console.log("Original failed URL:", originalSrc);
                                    
                                    // If the path contains /preview/user/images, transform it to the expected structure
                                    if (originalSrc.includes('/preview/user/images/')) {
                                      const parts = originalSrc.split('/');
                                      const userIndex = parts.indexOf('user');
                                      if (userIndex >= 0 && parts.length > userIndex + 3) {
                                        const username = parts[userIndex + 2]; // After 'user/images/'
                                        const projectName = parts[userIndex + 3]; // After username
                                        const filename = parts[parts.length - 1]; // Last part
                                        
                                        // Create alternative URL with fixed structure
                                        const newSrc = `/user/${username}/project/${projectName}/preview/${filename}`;
                                        console.log("Trying alternative URL:", newSrc);
                                        target.src = newSrc;
                                        return;
                                      }
                                    }
                                    
                                    // If still fails or doesn't match pattern, use default image
                                    setTimeout(() => {
                                      if (target.naturalWidth === 0) {
                                        target.src = "/images-for-project/default-project.jpg"; // Fallback image
                                      }
                                    }, 1000);
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                  URL: {generationResult.previewUrl}
                                </Typography>
                              </>
                            ) : (
                              <Typography color="error">Nie można załadować obrazu</Typography>
                            )}
                          </Box>
                        </Grid>
                        
                        {/* Image Metadata */}
                        <Grid item xs={12} md={4}>
                          <Paper 
                            elevation={0} 
                            variant="outlined"
                            sx={{ 
                              p: 2, 
                              borderRadius: 2,
                              height: '100%'
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Image Metadata
                            </Typography>
                            
                            <Box component="dl" sx={{ mt: 2 }}>
                              {/* Timestamp */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Generated At
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.metadata?.timestamp || '-'}
                              </Typography>
                              
                              {/* Generation Time */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Generation Time
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.elapsedTime} seconds
                              </Typography>
                              
                              {/* Resolution */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Resolution
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.metadata?.resolution || '-'}
                              </Typography>
                              
                              {/* Model */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Model
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.metadata?.modelName || '-'}
                              </Typography>
                              
                              {/* Body Part */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Body Part
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {selections.bodyPart === 'top' ? 'Góra / Top' : 
                                 selections.bodyPart === 'bottom' ? 'Dół / Bottom' : 
                                 'Całe Ciało / Full Body'}
                              </Typography>
                              
                              {/* Garment Type */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Garment Type
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.metadata?.garmentType || '-'}
                              </Typography>
                              
                              {/* Garment File */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Garment File
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}
                                style={{ 
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                {generationResult.metadata?.garmentFile || '-'}
                              </Typography>
                              
                              {/* Studio */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Studio
                              </Typography>
                              <Typography component="dd" variant="body2" sx={{ mb: 1.5 }}>
                                {generationResult.metadata?.studio || '-'}
                              </Typography>
                              
                              {/* Quality */}
                              <Typography component="dt" variant="caption" color="text.secondary">
                                Quality
                              </Typography>
                              <Typography component="dd" variant="body2">
                                {generationResult.metadata?.quality === 'high' ? 'High' : 'Low'}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Container>
    </ErrorBoundary>
  )
}

const DesignerPage: React.FC = () => {
  const { user, logout, isAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get the mode from URL query parameters
  const queryParams = new URLSearchParams(location.search)
  const mode = queryParams.get('mode') || 'on-model'
  
  const handleBackClick = () => {
    // Navigate back to the correct mode's project selection
    navigate(`/projects/${mode}`)
  }
  
  // Determine title based on mode
  const getTitle = () => {
    if (mode === 'on-model') {
      return "Fashion Babe Designer - Zdjęcia na modelu";
    } else if (mode === 'flat-lay') {
      return "Fashion Babe Designer - Wirtualne modele";
    }
    return "Fashion Babe Designer";
  };
  
  return (
    <ProjectProvider>
      <DesignerProvider>
        <AppHeader 
          title={getTitle()}
          username={user?.username} 
          showAdminButton={isAdmin}
          showBackButton
          onBackClick={handleBackClick}
          onLogout={logout}
        />
        <DesignerContent />
      </DesignerProvider>
    </ProjectProvider>
  )
}

export default DesignerPage
